import { getActiveFranchiseId, getApiBaseUrlOverride, getAuthToken } from './auth-storage'
import { ApiError, ResponseParseError } from './errors'
import { notifyUnauthorized } from './unauthorized'

export type ResponseBodyType = 'json' | 'text' | 'blob' | 'auto'

export type CustomFetchOptions = RequestInit & {
  responseType?: ResponseBodyType
}

const NO_BODY_STATUS = new Set([204, 205, 304])
const JSON_ACCEPT = 'application/json, application/problem+json'

/**
 * A 401 from these never means "your session ended" — `/auth/me` is what the
 * unauthorized handler itself calls to decide, and treating its 401 as a
 * logout signal would make the handler recurse. Ported verbatim from the
 * legacy `AUTH_PATHS` in `manager/src/lib/api-fetch.ts`, re-prefixed to v1.
 */
const SESSION_PROBE_PATHS = [
  '/api/v1/auth/login',
  '/api/v1/auth/setup',
  '/api/v1/auth/register',
  '/api/v1/auth/my-permissions',
  '/api/v1/auth/me',
  '/api/v1/central/auth/login',
]

function isSessionProbePath(url: string): boolean {
  return SESSION_PROBE_PATHS.some((path) => url.includes(path))
}

/**
 * Dev serves the app through a Vite proxy and prod serves it same-origin, so a
 * relative path is right in both. The only absolute case is a Control Tower
 * jump, which stores the target base itself.
 */
function resolveUrl(requestPath: string): string {
  const override = getApiBaseUrlOverride()
  if (!override) return requestPath
  if (typeof window !== 'undefined' && override === window.location.origin) return requestPath
  if (!requestPath.startsWith('/api/')) return requestPath
  return override + requestPath
}

function buildHeaders(init: CustomFetchOptions): Headers {
  const headers = new Headers(init.headers)

  // Both auth paths, exactly as legacy: the `token` cookie rides on
  // `credentials: include`, the bearer is attached here. An explicit header
  // from the caller always wins.
  if (!headers.has('authorization')) {
    const token = getAuthToken()
    if (token) headers.set('authorization', `Bearer ${token}`)
  }

  if (!headers.has('x-franchise-id')) {
    const franchiseId = getActiveFranchiseId()
    if (franchiseId) headers.set('x-franchise-id', franchiseId)
  }

  if (typeof init.body === 'string' && !headers.has('content-type') && looksLikeJson(init.body)) {
    headers.set('content-type', 'application/json')
  }

  if (!headers.has('accept')) headers.set('accept', JSON_ACCEPT)

  return headers
}

function looksLikeJson(text: string): boolean {
  const trimmed = text.trimStart()
  return trimmed.startsWith('{') || trimmed.startsWith('[')
}

function stripByteOrderMark(text: string): string {
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text
}

function mediaTypeOf(headers: Headers): string | null {
  const value = headers.get('content-type')
  return value ? (value.split(';', 1)[0] ?? '').trim().toLowerCase() : null
}

function isJsonMediaType(mediaType: string | null): boolean {
  return mediaType === 'application/json' || Boolean(mediaType?.endsWith('+json'))
}

function isTextMediaType(mediaType: string | null): boolean {
  if (!mediaType) return false
  return (
    mediaType.startsWith('text/') ||
    mediaType === 'application/xml' ||
    mediaType === 'application/x-www-form-urlencoded' ||
    mediaType.endsWith('+xml')
  )
}

function hasNoBody(response: Response, method: string): boolean {
  if (method === 'HEAD') return true
  if (NO_BODY_STATUS.has(response.status)) return true
  if (response.headers.get('content-length') === '0') return true
  return response.body == null
}

function inferResponseType(response: Response): Exclude<ResponseBodyType, 'auto'> {
  const mediaType = mediaTypeOf(response.headers)
  if (isJsonMediaType(mediaType)) return 'json'
  if (isTextMediaType(mediaType) || mediaType == null) return 'text'
  return 'blob'
}

async function parseSuccessBody(
  response: Response,
  responseType: ResponseBodyType,
  request: { method: string; url: string },
): Promise<unknown> {
  if (hasNoBody(response, request.method)) return null

  const effectiveType = responseType === 'auto' ? inferResponseType(response) : responseType

  if (effectiveType === 'blob') return response.blob()

  const raw = await response.text()
  const normalized = stripByteOrderMark(raw)
  if (normalized.trim() === '') return null
  if (effectiveType === 'text') return normalized

  try {
    return JSON.parse(normalized)
  } catch (cause) {
    throw new ResponseParseError(response, raw, cause, request)
  }
}

/**
 * Error bodies are read as leniently as legacy did: the backend's envelope is
 * `{ error, code? }` with a Slovak message that gets shown to staff as-is
 * (BACKEND.md §8), but a proxy timing out in front of it answers HTML, and
 * that must still surface as an ApiError rather than a parse crash.
 */
async function parseErrorBody(response: Response, method: string): Promise<unknown> {
  if (hasNoBody(response, method)) return null

  const mediaType = mediaTypeOf(response.headers)
  if (mediaType && !isJsonMediaType(mediaType) && !isTextMediaType(mediaType)) {
    return response.blob()
  }

  const raw = await response.text()
  const normalized = stripByteOrderMark(raw)
  if (normalized.trim() === '') return null

  if (isJsonMediaType(mediaType) || looksLikeJson(normalized)) {
    try {
      return JSON.parse(normalized)
    } catch {
      return raw
    }
  }

  return raw
}

/**
 * The single door to the AGOS API (FRONTEND.md §6). Every generated hook calls
 * this; nothing in the app calls `fetch` directly, and lint enforces it.
 */
export async function customFetch<T>(
  requestPath: string,
  options: CustomFetchOptions = {},
): Promise<T> {
  const { responseType = 'auto', headers: _ignoredHeaders, ...init } = options
  const method = (init.method ?? 'GET').toUpperCase()
  const url = resolveUrl(requestPath)

  const response = await fetch(url, {
    ...init,
    method,
    headers: buildHeaders(options),
    // The staff session also lives in an httpOnly `token` cookie.
    credentials: init.credentials ?? 'include',
  })

  if (!response.ok) {
    if (response.status === 401 && !isSessionProbePath(url)) {
      notifyUnauthorized()
    }
    throw new ApiError(response, await parseErrorBody(response, method), { method, url })
  }

  return (await parseSuccessBody(response, responseType, { method, url })) as T
}

export default customFetch
